import { UploadResponse } from "./use-file-upload-mutations";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  useUploadMultiple,
  useUploadSingle,
} from "./use-file-upload-mutations";

export interface UploadProgress {
  file: File;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  result?: UploadResponse["data"];
}

export interface UseFileUploadOptions {
  folder?: string;
  onUploadStart?: (files: File[]) => void;
  onUploadComplete?: (results: UploadResponse["data"][]) => void;
  onUploadError?: (errors: string[]) => void;
  onProgress?: (progress: UploadProgress[]) => void;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize React Query mutations
  const uploadSingleMutation = useUploadSingle({
    folder: options.folder || "uploads",
    onSuccess: (data) => {
      // Handle success in the uploadFiles function
    },
    onError: (error) => {
      // Handle error in the uploadFiles function
    },
  });

  const uploadMultipleMutation = useUploadMultiple({
    folder: options.folder || "uploads",
    onSuccess: (data) => {
      // Handle success in the uploadFiles function
    },
    onError: (error) => {
      // Handle error in the uploadFiles function
    },
  });

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setIsUploading(true);
      options.onUploadStart?.(files);

      // Initialize progress tracking
      const initialProgress: UploadProgress[] = files.map((file) => ({
        file,
        progress: 0,
        status: "pending",
      }));

      setUploadProgress(initialProgress);

      try {
        let results: UploadResponse["data"][] = [];
        let errors: string[] = [];

        if (files.length === 1) {
          // Single file upload
          const file = files[0];
          if (!file) return;

          // Update progress to uploading
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.file === file ? { ...p, status: "uploading", progress: 50 } : p
            )
          );

          try {
            const response = await uploadSingleMutation.mutateAsync({
              file,
              folder: options.folder || "uploads",
            });
            results.push(response.data);

            // Update progress to success
            setUploadProgress((prev) =>
              prev.map((p) =>
                p.file === file
                  ? {
                      ...p,
                      status: "success",
                      progress: 100,
                      result: response.data,
                    }
                  : p
              )
            );
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.message || error.message || "Upload failed";
            errors.push(`${file?.name || "Unknown file"}: ${errorMessage}`);

            // Update progress to error
            setUploadProgress((prev) =>
              prev.map((p) =>
                p.file === file
                  ? { ...p, status: "error", progress: 0, error: errorMessage }
                  : p
              )
            );
          }
        } else {
          // Multiple file upload
          setUploadProgress((prev) =>
            prev.map((p) => ({ ...p, status: "uploading", progress: 50 }))
          );

          try {
            const response = await uploadMultipleMutation.mutateAsync({
              files,
              folder: options.folder || "uploads",
            });
            results = response.data.successful;
            errors = response.data.failed.map(
              (f) => `${f.originalName}: ${f.error}`
            );

            // Update progress for successful uploads
            setUploadProgress((prev) =>
              prev.map((p) => {
                const successful = response.data.successful.find(
                  (s) => s.originalName === p.file.name
                );
                const failed = response.data.failed.find(
                  (f) => f.originalName === p.file.name
                );

                if (successful) {
                  return {
                    ...p,
                    status: "success",
                    progress: 100,
                    result: successful,
                  };
                } else if (failed) {
                  return {
                    ...p,
                    status: "error",
                    progress: 0,
                    error: failed.error,
                  };
                }
                return p;
              })
            );
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.message || error.message || "Upload failed";
            errors.push(errorMessage);

            // Update all to error
            setUploadProgress((prev) =>
              prev.map((p) => ({
                ...p,
                status: "error",
                progress: 0,
                error: errorMessage,
              }))
            );
          }
        }

        // Call callbacks
        if (results.length > 0) {
          options.onUploadComplete?.(results);
          toast.success(`${results.length} file(s) uploaded successfully`);
        }

        if (errors.length > 0) {
          options.onUploadError?.(errors);
          toast.error(`Upload errors:\n${errors.join("\n")}`);
        }

        // Update progress callback
        options.onProgress?.(uploadProgress);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || error.message || "Upload failed";
        options.onUploadError?.([errorMessage]);
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [options, uploadSingleMutation, uploadMultipleMutation, uploadProgress]
  );

  const clearProgress = useCallback(() => {
    setUploadProgress([]);
  }, []);

  const removeProgressItem = useCallback((file: File) => {
    setUploadProgress((prev) => prev.filter((p) => p.file !== file));
  }, []);

  return {
    uploadFiles,
    uploadProgress,
    isUploading:
      isUploading ||
      uploadSingleMutation.isPending ||
      uploadMultipleMutation.isPending,
    clearProgress,
    removeProgressItem,
    // Expose mutation states for advanced usage
    uploadSingleMutation,
    uploadMultipleMutation,
  };
}
